import * as THREE from 'three';
import { SKIN_COLOR, LEGS_COLOR, BREATHING_SPEED, BREATHING_AMOUNT } from '@/utils/constants';

/**
 * Lightweight Hatchling character for the homepage diorama.
 * Same geometry as the interior Worker but stripped of walk system,
 * action registry, labels, and state machine.
 */

// Body color variants for team diversity
export const BODY_COLORS = [
  0x1a1a4a, // deep navy (original)
  0x4a1a4a, // deep purple
  0x1a4a4a, // deep teal
  0x4a3a1a, // deep brown
];

interface CharacterParts {
  body: THREE.Mesh;
  headGroup: THREE.Group;
  leftArm: THREE.Mesh;
  rightArm: THREE.Mesh;
}

// Store references to animatable parts keyed by group uuid
const partsMap = new Map<string, CharacterParts>();

/**
 * Create a Hatchling character mesh group.
 * Same primitives as Worker: sphere head, capsule body, cylinder legs/arms, dot eyes, torus smile.
 */
export function createHomepageCharacter(bodyColor: number = BODY_COLORS[0]): THREE.Group {
  const group = new THREE.Group();

  const skinMat = new THREE.MeshStandardMaterial({ color: SKIN_COLOR });
  const bodyMat = new THREE.MeshStandardMaterial({ color: bodyColor });
  const legsMat = new THREE.MeshStandardMaterial({ color: LEGS_COLOR });

  // Legs
  const legGeo = new THREE.CylinderGeometry(0.05, 0.05, 0.5, 8);
  const leftLeg = new THREE.Mesh(legGeo, legsMat);
  leftLeg.position.set(-0.08, 0.25, 0);
  leftLeg.castShadow = true;
  group.add(leftLeg);
  const rightLeg = new THREE.Mesh(legGeo, legsMat);
  rightLeg.position.set(0.08, 0.25, 0);
  rightLeg.castShadow = true;
  group.add(rightLeg);

  // Body
  const bodyGeo = new THREE.CapsuleGeometry(0.125, 0.35, 4, 8);
  const body = new THREE.Mesh(bodyGeo, bodyMat);
  body.position.y = 0.75;
  body.castShadow = true;
  group.add(body);

  // Arms
  const armGeo = new THREE.CylinderGeometry(0.04, 0.035, 0.35, 6);

  const leftArm = new THREE.Mesh(armGeo, skinMat);
  leftArm.position.set(-0.2, 0.7, 0);
  leftArm.rotation.z = 0.15;
  leftArm.castShadow = true;
  group.add(leftArm);

  const rightArm = new THREE.Mesh(armGeo, skinMat);
  rightArm.position.set(0.2, 0.7, 0);
  rightArm.rotation.z = -0.15;
  rightArm.castShadow = true;
  group.add(rightArm);

  // Head group
  const headGroup = new THREE.Group();
  headGroup.position.y = 1.15;
  group.add(headGroup);

  const headGeo = new THREE.SphereGeometry(0.2, 16, 16);
  const head = new THREE.Mesh(headGeo, skinMat);
  head.castShadow = true;
  headGroup.add(head);

  // Eyes
  const eyeMat = new THREE.MeshStandardMaterial({ color: 0x222222 });
  const eyeGeo = new THREE.SphereGeometry(0.03, 8, 8);
  const leftEye = new THREE.Mesh(eyeGeo, eyeMat);
  leftEye.position.set(-0.07, 0.04, 0.17);
  headGroup.add(leftEye);
  const rightEye = new THREE.Mesh(eyeGeo, eyeMat);
  rightEye.position.set(0.07, 0.04, 0.17);
  headGroup.add(rightEye);

  // Smile
  const smileGeo = new THREE.TorusGeometry(0.04, 0.01, 4, 8, Math.PI);
  const smileMat = new THREE.MeshStandardMaterial({ color: 0xcc8866 });
  const smile = new THREE.Mesh(smileGeo, smileMat);
  smile.position.set(0, -0.04, 0.17);
  smile.rotation.x = Math.PI;
  headGroup.add(smile);

  // Store parts for animation
  partsMap.set(group.uuid, { body, headGroup, leftArm, rightArm });

  return group;
}

/**
 * Idle breathing animation — gentle bob + subtle arm sway.
 */
export function animateCharacterIdle(group: THREE.Group, elapsed: number): void {
  const parts = partsMap.get(group.uuid);
  if (!parts) return;

  group.position.y = (group.userData.baseY ?? group.position.y) +
    Math.sin(elapsed * BREATHING_SPEED) * BREATHING_AMOUNT;

  // Subtle arm sway
  parts.leftArm.rotation.x = Math.sin(elapsed * 0.5) * 0.03;
  parts.rightArm.rotation.x = Math.sin(elapsed * 0.5 + 0.5) * 0.03;
  parts.leftArm.rotation.z = 0.15;
  parts.rightArm.rotation.z = -0.15;
  parts.leftArm.position.z = 0;
  parts.rightArm.position.z = 0;

  // Reset head
  parts.headGroup.rotation.x = 0;
  parts.headGroup.rotation.y = 0;
  parts.headGroup.position.z = 0;
  parts.body.position.z = 0;
}

/**
 * Typing animation — arms forward, alternating, head tilted down.
 */
export function animateCharacterTyping(group: THREE.Group, elapsed: number): void {
  const parts = partsMap.get(group.uuid);
  if (!parts) return;

  const bobZ = Math.sin(elapsed * Math.PI * 4) * 0.01;
  parts.body.position.z = bobZ;
  parts.headGroup.position.z = bobZ * 0.5;
  parts.headGroup.rotation.x = 0.1;
  parts.headGroup.rotation.y = 0;

  // Arms forward, alternating typing
  parts.leftArm.rotation.z = -0.2;
  parts.rightArm.rotation.z = 0.2;
  parts.leftArm.rotation.x = -0.6 + Math.sin(elapsed * 8) * 0.15;
  parts.rightArm.rotation.x = -0.6 + Math.sin(elapsed * 8 + Math.PI) * 0.15;
  parts.leftArm.position.z = 0.1;
  parts.rightArm.position.z = 0.1;
}

/**
 * Dispose character and clean up parts map.
 */
export function disposeCharacter(group: THREE.Group): void {
  partsMap.delete(group.uuid);
  group.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      child.geometry?.dispose();
      if (child.material instanceof THREE.Material) {
        child.material.dispose();
      }
    }
  });
}
